<template>
  <template v-if="isEvaluating">
    <div class="row">
      <div class="col">
        <p>Evaluating...</p>
      </div>
    </div>
    <div class="row">
      <div class="col text-center">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>
    </div>
  </template>
  <template v-else>
    <div class="row">
      <div class="col">
        <template v-if="isViable">
          <p>
            It looks like your plan would still be expected to work if you used
            {{ canBuyGuaranteedIncomeResponse.percentAllocatedFromPots }}% of
            your pots to buy some guaranteed income, giving you less risk of
            running out of money in retirement.
          </p>
          <p>
            You can investigate this further in our Dashboard, Track and Help page.
          </p>
          <p>Do you want to include this in your plan?</p>
        </template>
        <template v-else>
          <p>
            Using {{ canBuyGuaranteedIncomeResponse.percentAllocatedFromPots }}%
            of your non protected pension pots to buy some guaranteed income at
            retirement will lead to shortfalls. You can investigate different 
            percentages in our Dashboard, Track and Help page.
          </p>
        </template>
      </div>
    </div>
  </template>
  <div v-if="hasError" class="row alert alert-info mx-1 px-0">
    <div class="col">
      <p>{{ errorMessage }}</p>
    </div>
  </div>
  <div class="d-grid gap-2 d-md-flex justify-content-md-end mt-4">
    <button
      type="button"
      v-if="showYesButton"
      :disabled="!yesBtnEnabled"
      class="btn px-5 btn-secondary"
      aria-label="Yes"
      @click="onYesClicked"
    >
      Yes
      <loading-spinner :visible="implementingSolution" />
    </button>
    <button
      type="button"
      class="btn px-5 btn-secondary"
      aria-label="No"
      @click="$emit('check-finished', false)"
    >
      {{ noButtonText }}
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { app } from "@/guiide/ts/app";
import api from "@/guiide/ts/api/api";
import { CanBuyGuaranteedIncomeResponse } from "@/guiide/ts/response/canBuyGuaranteedIncomeResponse";
import HttpStatusCode from "@/shared/ts/httpStatusCode";
import LoadingSpinner from "@/shared/components/LoadingSpinner.vue";

export default defineComponent({
  name: "GuaranteedIncomeChecker",
  emits: ["check-finished"],
  components: {
    LoadingSpinner
  },
  data() {
    return {
      isEvaluating: true,
      yesBtnEnabled: true,
      canBuyGuaranteedIncomeResponse: {
        isViable: false,
        percentAllocatedFromPots: 0
      } as CanBuyGuaranteedIncomeResponse,
      implementingSolution: false,
      errorMessage: ""
    };
  },
  computed: {
    isViable: function() {
      return this.canBuyGuaranteedIncomeResponse.isViable;
    },
    showYesButton: function() {
      return !this.isEvaluating && this.isViable && !this.hasError;
    },
    noButtonText: function() {
      if (this.isEvaluating || this.hasError) {
        return "Close";
      } else {
        return this.isViable ? "No" : "Close";
      }
    },
    hasError: function(): boolean {
      return this.errorMessage.length > 0;
    }
  },
  methods: {
    async checkIfCanBuyGuaranteedIncome() {
      this.initialise();
      this.canBuyGuaranteedIncomeResponse = await api.canBuyGuaranteedIncome();

      if (this.canBuyGuaranteedIncome != HttpStatusCode.Ok) {
        this.errorMessage = this.canBuyGuaranteedIncomeResponse.message;
      }

      this.isEvaluating = false;
    },
    async onYesClicked() {
      this.implementingSolution = true;
      this.yesBtnEnabled = false;
      this.errorMessage = "";

      const responseTracker = await app.applyGuaranteedIncome(
        null,
        null,
        "GIDesign"
      );

      if (responseTracker.isOk) {
        this.$emit("check-finished", true);
        return;
      }

      this.implementingSolution = false;
      this.yesBtnEnabled = true;

      if (responseTracker.trackerWillHandleFailure) {
        responseTracker.handleFailure();
      } else {
        this.errorMessage = responseTracker.message;
      }
    },
    initialise() {
      this.isEvaluating = true;
      this.yesBtnEnabled = true;
      this.implementingSolution = false;
    }
  }
});
</script>
